import type { AppliedFilterInterface, FilterInterface } from "@shopify/polaris";
import { useMemo } from "react";
import type { BaseQueryParams, Option, RangeFilter } from "../api_utils/types";
import { useArrayHandler, useRangeHandler, useStringHandler } from "./useHandlers";
import { moreThanLessThanLabel } from "../components/common/Filters/label_tools";
import { isEmpty } from "../helper_functions/utils";
import { forceNumber } from "../api_utils/requests";

const useAppliedFilter = (key: string, label: string, onRemove: () => void): AppliedFilterInterface => {
  return useMemo(
    () => ({
      key,
      label,
      onRemove,
    }),
    [key, label, onRemove],
  );
};

const useFilterComponent = (key: string, label: string): Partial<FilterInterface> => {
  return useMemo(
    () => ({
      key,
      label,
    }),
    [key, label],
  );
};

// These field hooks will manage everything related to a single filter in an IndexFilters component

export const useMoreThanLessThanField = <E extends string | number>({
  searchParams,
  key,
  label,
  valuePrefix,
  valueSuffix,
}: {
  searchParams: URLSearchParams;
  key: string;
  label: string;
  valuePrefix?: string;
  valueSuffix?: string;
}): {
  queryParam: BaseQueryParams;
  value: RangeFilter<E>;
  setValue: (value: RangeFilter<E>) => void;
  handleRemove: () => void;
  fullLabel: string;
  appliedFilter: AppliedFilterInterface | undefined;
  filterComponent: Partial<FilterInterface>;
} => {
  const { value, setValue, handleRemove } = useRangeHandler<E, RangeFilter<E>>(searchParams, key);
  const fullLabel = moreThanLessThanLabel(value, label, valuePrefix, valueSuffix);

  const queryParam = useMemo(
    () => ({
      [key]: {
        more_than: forceNumber(value.more_than),
        less_than: forceNumber(value.less_than),
      },
    }),
    [value.more_than, value.less_than, key],
  );

  const appliedFilter = useAppliedFilter(key, fullLabel, handleRemove);

  const filterComponent = useFilterComponent(key, label);

  return {
    queryParam,
    value,
    setValue,
    handleRemove,
    fullLabel,
    appliedFilter: !isEmpty(value.more_than) || !isEmpty(value.less_than) ? appliedFilter : undefined,
    filterComponent,
  };
};

export const useIntSelect = ({
  searchParams,
  labelPrefix,
  key,
  options,
  allValue,
}: {
  searchParams: URLSearchParams;
  labelPrefix: string;
  key: string;
  options: Option[];
  allValue?: string;
}): {
  queryParam: BaseQueryParams;
  value: string;
  setValue: (value: string) => void;
  handleRemove: () => void;
  label: string;
  appliedFilter: AppliedFilterInterface | undefined;
  filterComponent: Partial<FilterInterface>;
} => {
  const { value, setValue, handleRemove } = useStringHandler(searchParams, key, allValue);
  const choice = options.find((sup) => sup.value === value)?.label || value;
  const fullLabel = `${labelPrefix} is ${choice}`;

  const appliedFilter = useAppliedFilter(key, fullLabel, handleRemove);

  const filterComponent = useFilterComponent(key, labelPrefix || "");

  const queryParam = useMemo(
    () => ({
      [key]: forceNumber(value),
    }),
    [value, key],
  );

  return {
    queryParam,
    value,
    setValue,
    handleRemove,
    label: fullLabel,
    appliedFilter: value ? appliedFilter : undefined,
    filterComponent,
  };
};

export const useStringSelect = <T extends string>({
  searchParams,
  labelPrefix,
  key,
  options,
  allValue,
  defaultValue,
  filterLabel,
  onlyShowNonDefault = false,
}: {
  searchParams: URLSearchParams;
  labelPrefix?: string;
  key: string;
  options: { value: string; label: string }[];
  allValue?: string;
  defaultValue?: string;
  filterLabel?: string;
  onlyShowNonDefault?: boolean;
}): {
  queryParam: BaseQueryParams;
  value: T;
  setValue: (value: T) => void;
  handleRemove: () => void;
  label: string;
  appliedFilter: AppliedFilterInterface | undefined;
  filterComponent: Partial<FilterInterface>;
} => {
  const { value, setValue, handleRemove } = useStringHandler(searchParams, key, allValue, defaultValue, { onlyShowNonDefault, showAllValue: onlyShowNonDefault });
  const choice = options.find((sup) => sup.value === value)?.label || value;
  const fullLabel = labelPrefix ? `${labelPrefix} is ${choice}` : choice;

  const appliedFilter = useAppliedFilter(key, fullLabel, handleRemove);

  const filterComponent = useFilterComponent(key, filterLabel || labelPrefix || "");

  const queryParam = useMemo(
    () => ({
      [key]: value,
    }),
    [value, key],
  );

  return {
    queryParam,
    value: value as T,
    setValue,
    handleRemove,
    label: fullLabel,
    appliedFilter: value ? appliedFilter : undefined,
    filterComponent,
  };
};

export const useStringMultiSelect = ({
  searchParams,
  labelPrefix,
  key,
  options,
}: {
  searchParams: URLSearchParams;
  labelPrefix?: string;
  key: string;
  options: Option[];
}): {
  queryParam: BaseQueryParams;
  values: string[];
  setValues: (value: string[]) => void;
  handleAdd: (value: string) => void;
  handleRemove: () => void;
  label: string;
  appliedFilter: AppliedFilterInterface | undefined;
  filterComponent: Partial<FilterInterface>;
} => {
  const { values, setValues, handleAdd, handleRemove } = useArrayHandler<string>(searchParams, key);
  const choices = options.filter((sup) => values.includes(sup.value)).map((sup) => sup.label) || values;
  const fullLabel = `${labelPrefix}: ${choices.join(", ")}`;

  const appliedFilter = useAppliedFilter(key, fullLabel, handleRemove);

  const filterComponent = useFilterComponent(key, labelPrefix || "");

  const queryParam = useMemo(
    () => ({
      [key]: values,
    }),
    [values, key],
  );

  return {
    queryParam,
    values,
    setValues,
    handleAdd,
    handleRemove,
    label: fullLabel,
    appliedFilter: values.length > 0 ? appliedFilter : undefined,
    filterComponent,
  };
};
